import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import { initializeApiClient } from 'utils/apiClient';
import { apiRequestWithBackoff } from 'utils/apiService';
import logger from 'utils/logger';

export const TenantContext = createContext();

export const useTenantContext = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenantContext must be used within a TenantProvider');
  }
  return context;
};

// Backward compatibility alias
export const useTenant = useTenantContext;

export const TenantProvider = ({ children }) => {
  const { instance } = useMsal();
  const [tenants, setTenants] = useState([]);
  const [tenantDetails, setTenantDetails] = useState({});
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [, setLastFetch] = useState(null);
  const [, setCache] = useState({});
  const [isMainTenantPartner, setIsMainTenantPartner] = useState(false);
  const [autoProvisioned, setAutoProvisioned] = useState(false);

  // Cache configuration
  const CACHE_DURATION = 60 * 1000; // 60 seconds
  // eslint-disable-next-line no-unused-vars
  const CACHE_KEY = 'tenant_context';

  // Use refs for cache and coordination (stable across renders)
  const cacheTimestampRef = useRef(null);
  const loadPromiseRef = useRef(null);
  const isLoadingRef = useRef(false);
  const lastLoadAttemptRef = useRef(0);
  const failureCountRef = useRef(0);

  // Cache validation using ref (stable function)
  const isCacheValid = useCallback(() => {
    if (!cacheTimestampRef.current) return false;
    return Date.now() - cacheTimestampRef.current < CACHE_DURATION;
  }, []);

  const loadTenantContext = useCallback(async (forceRefresh = false) => {
    if (!instance || instance.getAllAccounts().length === 0) {
      return { tenants: [], tenantDetails: {}, permissions: {} };
    }

    // Check cache first (synchronous check before any async work)
    if (!forceRefresh && isCacheValid() && tenants.length > 0) {
      return { tenants, tenantDetails, permissions };
    }

    // Return existing promise if already loading (shared promise pattern)
    if (loadPromiseRef.current) {
      return loadPromiseRef.current;
    }

    // Circuit breaker: if we've failed too many times, wait longer
    const now = Date.now();
    const lastAttempt = lastLoadAttemptRef.current;
    const timeSinceLastAttempt = now - lastAttempt;
    
    if (failureCountRef.current >= 3 && timeSinceLastAttempt < 60000) {
      logger.warn('TENANT-CONTEXT', 'Too many failures, waiting before retry', { waitSeconds: Math.ceil((60000 - timeSinceLastAttempt) / 1000) });
      return { tenants: [], tenantDetails: {}, permissions: {} };
    }

    // Create single shared promise that all callers will use
    loadPromiseRef.current = (async () => {
      try {
        isLoadingRef.current = true;
        lastLoadAttemptRef.current = now;
        setLoading(true);
        setError(null);
        
        const apiClient = initializeApiClient(instance);

        // Load tenants and main tenant details in parallel
        const [tenantsRes, mainDetails] = await Promise.all([
          apiRequestWithBackoff(
            apiClient,
            'listWorkspaces',
            { method: 'CALL', params: { includeStats: true } }
          ).catch(err => {
            logger.warn('TENANT-CONTEXT', 'Failed to load tenants', { error: err.message });
            return { success: true, data: [] };
          }),
          apiRequestWithBackoff(apiClient, '/v1/tenants/main/details', { method: 'GET' }).catch(() => null)
        ]);
        
        // Normalize response structure - handle both direct response and wrapped response
        let normalizedResponse = tenantsRes;
        
        // Check for auto_provisioned flag in response
        const isAutoProvisioned = tenantsRes?.auto_provisioned === true || tenantsRes?.data?.auto_provisioned === true;
        if (isAutoProvisioned) {
          logger.info('TENANT-CONTEXT', 'Auto-provisioning detected in tenant context response');
          setAutoProvisioned(true);
        }
        
        // If response already has the correct structure {success: true, data: [...]}
        if (tenantsRes && tenantsRes.success === true && tenantsRes.data && Array.isArray(tenantsRes.data)) {
          // Response is already in the correct format
          normalizedResponse = {
            success: true,
            data: tenantsRes.data,
            container_missing: tenantsRes.container_missing || false,
            auto_provisioned: isAutoProvisioned
          };
        } else if (tenantsRes && !tenantsRes.hasOwnProperty('success')) {
          // If response doesn't have 'success', assume it's the data directly
          // Wrap it in the expected structure
          normalizedResponse = {
            success: true,
            data: Array.isArray(tenantsRes) ? tenantsRes : (tenantsRes.data || tenantsRes.tenants || []),
            container_missing: tenantsRes.container_missing || false,
            auto_provisioned: isAutoProvisioned
          };
        } else if (tenantsRes && tenantsRes.success && !tenantsRes.hasOwnProperty('data') && Array.isArray(tenantsRes)) {
          // If response has success but data is at root level
          normalizedResponse = {
            success: true,
            data: tenantsRes,
            container_missing: false,
            auto_provisioned: isAutoProvisioned
          };
        } else if (!tenantsRes) {
          // No response - create empty response
          normalizedResponse = {
            success: false,
            data: [],
            container_missing: false,
            auto_provisioned: false
          };
        }
        
        if (normalizedResponse.success && normalizedResponse.data && !normalizedResponse.container_missing) {
          const tenantsData = normalizedResponse.data;
          const tenantDetailsMap = {};
          const permissionsMap = {};
          
          // Build tenant details and permissions maps
          tenantsData.forEach(tenant => {
            tenantDetailsMap[tenant.id] = {
              id: tenant.id,
              name: tenant.name,
              description: tenant.description,
              status: tenant.status,
              is_main_tenant: tenant.is_main_tenant,
              tenant_type: tenant.tenant_type,
              tenant_category: tenant.tenant_category,
              is_partner: tenant.is_partner
            };
            
            // Extract permissions from tenant context if available
            permissionsMap[tenant.id] = tenant.permissions || ['read'];
          });
          
          setTenants(tenantsData);
          setTenantDetails(tenantDetailsMap);
          setPermissions(permissionsMap);
          const fetchTime = Date.now();
          setLastFetch(fetchTime);
          cacheTimestampRef.current = fetchTime;
          failureCountRef.current = 0;

          // Use main tenant details (already fetched in parallel) for partner flag
          if (mainDetails?.container_missing) {
            logger.warn('TENANT-CONTEXT', 'Container not yet initialized, skipping main tenant details');
            setIsMainTenantPartner(false);
          } else {
            const mainTenant = mainDetails?.data;
            const partner = !!(mainTenant && (mainTenant.tenant_category === 'partner' || mainTenant.is_partner === true));
            setIsMainTenantPartner(partner);
          }
          
          // Cache the result
          const cacheData = {
            tenants: tenantsData,
            tenantDetails: tenantDetailsMap,
            permissions: permissionsMap,
            timestamp: fetchTime
          };
          setCache(cacheData);
          
          return { tenants: tenantsData, tenantDetails: tenantDetailsMap, permissions: permissionsMap };
        } else if (normalizedResponse.container_missing) {
          // Container missing - return empty but don't count as failure
          logger.warn('TENANT-CONTEXT', 'Container not yet initialized, returning empty tenant context');
          setTenants([]);
          setTenantDetails({});
          setPermissions({});
          setIsMainTenantPartner(false);
          return { tenants: [], tenantDetails: {}, permissions: {} };
        }
        
        // If we got here, there was an error but not container missing
        failureCountRef.current++;
        return { tenants: [], tenantDetails: {}, permissions: {} };
      } catch (err) {
        logger.error('TENANT-CONTEXT', 'Error loading tenant context', { error: err.message });
        setError('Failed to load tenant context');
        setIsMainTenantPartner(false);
        failureCountRef.current++;
        return { tenants: [], tenantDetails: {}, permissions: {} };
      } finally {
        isLoadingRef.current = false;
        setLoading(false);
        loadPromiseRef.current = null; // Clear promise reference
      }
    })();
    
    return loadPromiseRef.current;
  }, [instance, isCacheValid]); // Removed problematic dependencies

  const refreshTenantContext = useCallback(() => {
    return loadTenantContext(true);
  }, [loadTenantContext]);

  const getTenantById = useCallback((tenantId) => {
    return tenantDetails[tenantId] || null;
  }, [tenantDetails]);

  const getTenantPermissions = useCallback((tenantId) => {
    return permissions[tenantId] || [];
  }, [permissions]);

  const hasPermission = useCallback((tenantId, permission) => {
    const tenantPermissions = getTenantPermissions(tenantId);
    return tenantPermissions.includes(permission) || tenantPermissions.includes('admin');
  }, [getTenantPermissions]);

  // Get current tenant (default to first tenant)
  const currentTenant = tenants.length > 0 ? tenants[0] : null;

  // Get tenant name by ID
  const getTenantName = useCallback((tenantId) => {
    const tenant = getTenantById(tenantId);
    return tenant ? tenant.name : null;
  }, [getTenantById]);

  // Load context when instance becomes available
  // Only depend on account count (stable check) to prevent unnecessary re-renders
  const accountCount = instance?.getAllAccounts().length || 0;
  useEffect(() => {
    if (instance && accountCount > 0) {
      loadTenantContext();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountCount]); // Only react when account count changes

  const value = useMemo(() => ({
    tenants,
    tenantDetails,
    permissions,
    loading,
    error,
    currentTenant,
    isMainTenantPartner,
    autoProvisioned,
    setAutoProvisioned,
    loadTenantContext,
    refreshTenantContext,
    getTenantById,
    getTenantName,
    getTenantPermissions,
    hasPermission,
    isCacheValid: isCacheValid()
  }), [
    tenants,
    tenantDetails,
    permissions,
    loading,
    error,
    currentTenant,
    isMainTenantPartner,
    autoProvisioned,
    loadTenantContext,
    refreshTenantContext,
    getTenantById,
    getTenantName,
    getTenantPermissions,
    hasPermission
  ]);

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
};